import { Linkified } from "../../components/Linkified";
import { Bell, BellOff, Check, ChevronLeft, ChevronRight, Clock3, MessageSquareText, Pencil, Play, RotateCcw, Send, ShieldCheck, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { montageApi, toTask } from "../../api";
import { ME_CLIENT, PEOPLE, PROJECTS, teamOf } from "../../data/people";
import { isPostItem, itemsToModerate, lastVersion, revisionAt } from "../../lib/domain";
import { notify } from "../../lib/notify";
import { agoLong } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { ImageCarousel } from "../../components/ImageCarousel";
import { useStableUrl } from "../../lib/useStableUrl";
import { applyVolume, getSavedVolume, RATES, rememberVolume, saveVolume, useSavedVolume } from "../../lib/volume";
import { formatDuration } from "../../lib/utils";
import { useStore } from "../../store";
import type { SubTask, Task } from "../../types";
import { usePush } from "../../lib/usePush";
import { emptyDrawing, FrameDrawing, hasDrawing } from "../../components/FrameDrawing";
import type { FrameDrawing as FrameDrawingValue } from "../../types";

type Row = { task: Task; item: SubTask };

/** Столько сервер ждёт после «Норм», прежде чем выложить ролик (MODERATION_UNDO_MS в montage.routes). */
const AUTO_PUBLISH_UNDO_MS = 30_000;

/** Модератор работает с роликами по одному. Ссылка из Telegram/PWA открывает нужный ролик. */
export function Moderation() {
  const now = useNow();
  const tasks = useStore((s) => s.tasks);
  const [params, setParams] = useSearchParams();
  const push = usePush();

  const queue = useMemo(() => {
    const rows: Row[] = [];
    for (const task of tasks) for (const item of itemsToModerate(task)) rows.push({ task, item });
    return rows.sort((a, b) => (lastVersion(a.item)?.at ?? 0) - (lastVersion(b.item)?.at ?? 0));
  }, [tasks]);
  const checked = useMemo(() => {
    const rows: Row[] = [];
    for (const task of tasks) for (const item of task.items) {
      if (item.mod?.by === ME_CLIENT && item.status !== "uploaded") rows.push({ task, item });
    }
    return rows.sort((a, b) => (b.item.mod?.at ?? 0) - (a.item.mod?.at ?? 0)).slice(0, 8);
  }, [tasks]);

  const selected = [...queue, ...checked].find(({ task, item }) => task.id === params.get("task") && item.id === params.get("item"));
  const open = (row: Row) => setParams({ task: row.task.id, item: row.item.id }, { replace: true });
  const close = () => setParams({}, { replace: true });
  const onVerdict = (row: Row) => {
    const next = queue.find((x) => x.item.id !== row.item.id);
    if (next) open(next);
    else close();
  };

  useEffect(() => {
    if ("setAppBadge" in navigator) {
      void (navigator as Navigator & { setAppBadge: (n: number) => Promise<void> }).setAppBadge(queue.length).catch(() => {});
    }
  }, [queue.length]);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-5 pb-10 pt-2 md:gap-7 md:pt-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Очередь проверки</p>
          <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.04em] md:text-[36px]">Модерация</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {queue.length ? `${queue.length} ${queue.length === 1 ? "ролик ждёт" : "роликов ждут"} вашего решения` : "Все ролики просмотрены"}
          </p>
        </div>
        <span className="flex size-12 items-center justify-center rounded-2xl bg-review-soft text-review-fg" aria-label={`${queue.length} на проверке`}>
          <Bell className="size-5" />
          {queue.length > 0 && <span className="ml-0.5 text-sm font-bold tabular-nums">{queue.length}</span>}
        </span>
      </header>

      {push.supported && (
        <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3.5 shadow-mxs sm:p-4">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-review-soft text-review-fg">
            {push.enabled ? <Bell className="size-5" /> : <BellOff className="size-5" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{push.enabled ? "Уведомления включены" : "Новые ролики без задержки"}</p>
            <p className="text-xs leading-5 text-muted-foreground">
              {push.enabled ? "Уведомление откроет конкретное видео. Значок приложения покажет число ожидающих." : "Получайте уведомления о новых сдачах, даже когда PWA закрыто. На iPhone сначала добавьте сайт на экран «Домой»."}
            </p>
          </div>
          <button type="button" onClick={() => void (push.enabled ? push.disable() : push.enable())} disabled={push.busy}
            className="shrink-0 rounded-full bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">
            {push.busy ? "…" : push.enabled ? "Выключить" : "Включить"}
          </button>
        </div>
      )}
      {push.error && <p role="alert" className="rounded-xl bg-rework-soft px-4 py-3 text-sm text-rework-fg">{push.error}</p>}

      {queue.length > 0 ? (
        <section aria-labelledby="moderation-queue" className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 id="moderation-queue" className="text-sm font-semibold">Смотреть сейчас</h2>
            <span className="text-xs text-muted-foreground">Старые сдачи выше</span>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {queue.map((row, index) => {
              const ver = lastVersion(row.item);
              const project = PROJECTS.find((p) => p.id === row.task.projectId)?.name;
              return (
                <button type="button" key={row.item.id} onClick={() => open(row)}
                  className="group flex min-h-[124px] items-center gap-4 rounded-[22px] border border-border bg-card p-3 text-left shadow-mxs transition hover:border-review-bd hover:shadow-mmd focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-review">
                  <span className="relative h-[100px] w-[64px] shrink-0 overflow-hidden rounded-xl bg-player">
                    <img src={ver?.poster ?? row.item.poster} alt="" className="size-full object-cover" />
                    <span className="absolute inset-0 grid place-items-center bg-black/20"><Play className="size-6 fill-white text-white" /></span>
                  </span>
                  <span className="min-w-0 flex-1 space-y-1.5">
                    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-review-fg">
                      <span>#{index + 1} в очереди</span><span>·</span><span>v{ver?.n ?? 1}</span>
                    </span>
                    <span className="block line-clamp-2 text-base font-semibold leading-snug">{row.item.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">{whoMade(row.task, project)}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock3 className="size-3" />{ver ? agoLong(ver.at, now) : ""} · {formatDuration(ver?.duration ?? 0)}</span>
                  </span>
                  <ChevronRight className="size-5 shrink-0 text-muted-foreground group-hover:text-review-fg" />
                </button>
              );
            })}
          </div>
        </section>
      ) : (
        <div className="grid min-h-52 place-content-center rounded-[24px] border border-dashed border-border bg-card/60 px-6 text-center">
          <ShieldCheck className="mx-auto mb-3 size-9 text-done-fg" />
          <p className="font-semibold">Очередь пуста</p>
          <p className="mt-1 text-sm text-muted-foreground">Новый ролик появится здесь после сдачи монтажёром.</p>
        </div>
      )}

      {checked.length > 0 && (
        <section aria-labelledby="moderation-checked" className="space-y-2 pt-2">
          <h2 id="moderation-checked" className="text-sm font-semibold text-muted-foreground">Недавно проверено</h2>
          {checked.map(({ task, item }) => (
            <button type="button" key={item.id} onClick={() => open({ task, item })} className="flex w-full items-center gap-3 rounded-xl bg-card/70 px-4 py-3 text-left text-sm hover:bg-card">
              {item.mod?.verdict === "ok" ? <Check className="size-4 shrink-0 text-done-fg" /> : <RotateCcw className="size-4 shrink-0 text-rework-fg" />}
              <span className="min-w-0 flex-1 truncate">{item.title}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{task.code} · {item.mod ? agoLong(item.mod.at, now) : ""}</span>
              {undoable(item) && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); void undoVerdict(task.id, item.id, item.title); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); void undoVerdict(task.id, item.id, item.title); } }}
                  className="shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium hover:bg-muted"
                >
                  Отменить
                </span>
              )}
            </button>
          ))}
        </section>
      )}

      {selected && (() => {
        // ‹ › и ←/→ — соседние ролики очереди (08.10.2026).
        const at = queue.findIndex((x) => x.item.id === selected.item.id);
        const prev = at > 0 ? queue[at - 1] : undefined;
        const next = at >= 0 ? queue[at + 1] : queue[0];
        return (
          <Review
            key={`${selected.item.id}-${lastVersion(selected.item)?.n}`}
            row={selected}
            queueLength={queue.length}
            position={at >= 0 ? at + 1 : null}
            onPrev={prev ? () => open(prev) : undefined}
            onNext={next && next.item.id !== selected.item.id ? () => open(next) : undefined}
            onClose={close}
            onVerdict={onVerdict}
          />
        );
      })()}
    </div>
  );
}

function Review({ row, queueLength, position, onPrev, onNext, onClose, onVerdict }: {
  row: Row; queueLength: number; position: number | null;
  onPrev?: () => void; onNext?: () => void; onClose: () => void; onVerdict: (row: Row) => void;
}) {
  const { task, item } = row;
  const ver = lastVersion(item);
  const video = useRef<HTMLVideoElement>(null);
  const note = useRef<HTMLInputElement>(null);
  const [time, setTime] = useState(0);
  const [slide, setSlide] = useState(0);
  const [slideSeek, setSlideSeek] = useState<{ t: number; n: number } | null>(null);
  const post = isPostItem(item);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState<{ tc: number; text: string; drawing?: FrameDrawingValue }[]>([]);
  const [drawingMode, setDrawingMode] = useState(false);
  const [draftDrawing, setDraftDrawing] = useState<FrameDrawingValue>(emptyDrawing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [videoLoaded, setVideoReady] = useState(false);
  // У поста картинками ждать нечего — решение доступно сразу.
  const videoReady = videoLoaded || Boolean(row.item.versions[row.item.versions.length - 1]?.images?.length);
  const [videoError, setVideoError] = useState(false);
  const project = PROJECTS.find((p) => p.id === task.projectId)?.name;
  const pending = item.status === "uploaded";

  useEffect(() => {
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) return;
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    };
    window.addEventListener("keydown", esc);
    return () => { document.body.style.overflow = old; window.removeEventListener("keydown", esc); };
  }, [onClose, onPrev, onNext]);

  const addNote = (e: React.FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value && !hasDrawing(draftDrawing)) return;
    setNotes((all) => [...all, {
      tc: post ? slide + 1 : Math.round(time),
      text: value || "Пометка на кадре",
      drawing: hasDrawing(draftDrawing) ? draftDrawing : undefined,
    }].sort((a, b) => a.tc - b.tc));
    setText("");
    setDrawingMode(false);
    setDraftDrawing(emptyDrawing());
    note.current?.focus();
  };
  const verdict = async (result: "ok" | "rework") => {
    if (!ver || !videoReady || busy || (result === "rework" && notes.length === 0)) return;
    setBusy(true);
    setError("");
    try {
      const response = await montageApi.moderate(task.id, item.id, { verdict: result, versionId: ver.id, items: result === "rework" ? notes : undefined });
      const saved = toTask(response.task);
      useStore.getState()._replace(task.id, saved);
      // Нажала не ту кнопку — «Отменить» прямо в тосте (09.10.2026). Ролик,
      // который сервер сам выложит в Instagram, ждёт там отмены столько же,
      // сколько живёт тост (29.10.2026).
      const mine = saved.items.find((i) => i.id === item.id);
      const auto = result === "ok" && mine?.status === "approved" && mine.autoPublishState === "waiting";
      notify(
        auto ? `«${item.title}» уйдёт в Instagram через ${AUTO_PUBLISH_UNDO_MS / 1000} с`
          : result === "ok" ? `«${item.title}» передан контент-мейкеру` : `Замечания к «${item.title}» отправлены монтажёру`,
        {
          icon: result === "ok" ? "check" : "rework",
          duration: auto ? AUTO_PUBLISH_UNDO_MS : 10000,
          undo: () => void undoVerdict(task.id, item.id, item.title),
        },
      );
      onVerdict(row);
    } catch (err) {
      setError((err as Error).message || "Не удалось отправить вердикт");
    } finally { setBusy(false); }
  };

  // Перечитывание очереди приносит заново подписанные ссылки — плеер не должен
  // из-за этого начинать ролик сначала.
  const videoSrc = useStableUrl(ver?.id, ver?.videoUrl);
  // Скорость: кнопки на экране — встроенный плеер iPhone её не меняет, а на
  // компьютере она спрятана в меню. Выбранная стоит на всех роликах.
  const sound = useSavedVolume();
  const setRate = (rate: number) => {
    saveVolume({ rate });
    if (video.current) video.current.playbackRate = rate;
  };
  const posterSrc = useStableUrl(ver?.id, ver?.poster ?? item.poster);

  return (
    <div role="dialog" aria-modal="true" aria-label={`Проверка: ${item.title}`} className="montage-root fixed inset-0 z-[70] flex flex-col bg-background md:items-center md:justify-center md:bg-overlay md:p-5">
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-background md:max-h-[min(900px,95svh)] md:max-w-5xl md:flex-none md:rounded-[26px] md:shadow-mlg">
        <header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3 md:px-6">
          <button type="button" onClick={onClose} aria-label="Закрыть" className="grid size-10 shrink-0 place-items-center rounded-full bg-muted"><X className="size-5" /></button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-muted-foreground">{whoMade(task, project)} · {task.code} · v{ver?.n}</p>
            <h2 className="truncate text-base font-semibold">{item.title}</h2>
          </div>
          <button type="button" onClick={onPrev} disabled={!onPrev} aria-label="Предыдущий ролик" className="grid size-9 shrink-0 place-items-center rounded-full bg-muted disabled:opacity-40"><ChevronLeft className="size-5" /></button>
          <span className="shrink-0 rounded-full bg-review-soft px-3 py-1.5 text-xs font-semibold tabular-nums text-review-fg">{position ? `${position} из ${queueLength}` : `${queueLength} в очереди`}</span>
          <button type="button" onClick={onNext} disabled={!onNext} aria-label="Следующий ролик" className="grid size-9 shrink-0 place-items-center rounded-full bg-muted disabled:opacity-40"><ChevronRight className="size-5" /></button>
        </header>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
          <div className="flex min-h-[min(51svh,440px)] shrink-0 items-center justify-center bg-player p-2 md:min-h-0 md:w-[48%] md:p-5">
            {ver?.images?.length ? (
              // Пост картинками: листаем, кнопки решения доступны сразу (08.10.2026).
              <ImageCarousel images={ver.images} onIndex={setSlide} seek={slideSeek} className="h-full w-full bg-transparent p-0" />
            ) : (
            <div className="relative max-h-[min(49svh,430px)] max-w-full overflow-hidden rounded-xl bg-black md:max-h-[70svh]">
              <video ref={video} src={videoSrc} poster={posterSrc} controls={!drawingMode} playsInline preload="metadata"
                onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => { applyVolume(e.currentTarget, getSavedVolume()); setVideoReady(true); setVideoError(false); }}
                // Громкость, выставленная на одном ролике, — на всех следующих.
                onVolumeChange={(e) => rememberVolume(e.currentTarget)}
                onRateChange={(e) => saveVolume({ rate: e.currentTarget.playbackRate })}
                onError={() => { setVideoReady(false); setVideoError(true); }}
                className="max-h-[min(49svh,430px)] w-auto max-w-full bg-black md:max-h-[70svh]" />
              {drawingMode && <FrameDrawing value={draftDrawing} editable onChange={setDraftDrawing} />}
              {drawingMode && (
                <span className="pointer-events-none absolute left-2 top-2 z-30 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white">
                  Рисуйте пальцем
                </span>
              )}
            </div>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-5 p-4 pb-7 md:min-h-0 md:overflow-y-auto md:p-6">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">Монтажёр · <strong className="font-semibold text-foreground">{PEOPLE[task.editor].name}</strong></span>
              <span className="font-mono text-xs text-muted-foreground">{post ? `слайд ${slide + 1} из ${ver?.images?.length ?? 1}` : `${formatDuration(time)} / ${formatDuration(ver?.duration ?? 0)}`}</span>
            </div>
            {!post && (
            <div role="group" aria-label="Скорость" className="flex items-center gap-1.5 text-sm">
              <span className="mr-1 text-xs text-muted-foreground">Скорость</span>
              {RATES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRate(r)}
                  aria-pressed={sound.rate === r}
                  className={sound.rate === r
                    ? "h-8 rounded-full bg-foreground px-3 text-xs font-semibold text-background"
                    : "h-8 rounded-full border border-border px-3 text-xs font-semibold text-muted-foreground"}
                >
                  {String(r).replace(".", ",")}×
                </button>
              ))}
            </div>
            )}
            {videoError && <p role="alert" className="rounded-xl bg-rework-soft p-3 text-sm text-rework-fg">Видео не загрузилось. Закройте проверку, обновите очередь и попробуйте снова.</p>}
            {ver?.comment && <div className="rounded-2xl bg-muted p-3 text-sm"><strong className="block pb-1 text-xs">Комментарий монтажёра</strong>{ver.comment}</div>}
            {item.brief && <details className="rounded-2xl border border-border p-3 text-sm"><summary className="cursor-pointer font-semibold">Бриф</summary><p className="whitespace-pre-wrap pt-2 text-muted-foreground [overflow-wrap:anywhere]"><Linkified text={item.brief} /></p></details>}
            {item.revisions.filter((r) => r.round === (ver?.n ?? 0) - 1).length > 0 && (
              <div className="space-y-2"><h3 className="text-sm font-semibold">Правки к предыдущей версии</h3>
                {item.revisions.filter((r) => r.round === (ver?.n ?? 0) - 1).map((r) => <p key={r.id} className="text-sm"><span className="mr-2 font-mono text-xs text-muted-foreground">{revisionAt(r.tc, post)}</span>{r.text}</p>)}
              </div>
            )}
            {item.revisions.filter((r) => r.round === (ver?.n ?? 0)).length > 0 && (
              <div className="space-y-2"><h3 className="text-sm font-semibold">Отправленные замечания</h3>
                {item.revisions.filter((r) => r.round === (ver?.n ?? 0)).map((r) => <p key={r.id} className="text-sm"><span className="mr-2 font-mono text-xs text-muted-foreground">{revisionAt(r.tc, post)}</span>{r.text}</p>)}
              </div>
            )}
            {pending && <div className="space-y-3">
              <div className="flex items-center gap-2"><MessageSquareText className="size-4 text-rework-fg" /><h3 className="text-sm font-semibold">{post ? "Замечания по слайдам" : "Замечания с таймкодом"}</h3></div>
              {notes.map((n, index) => <div key={`${n.tc}-${index}`} className="flex items-start gap-2 rounded-xl bg-rework-soft px-3 py-2 text-sm">
                <button type="button" onClick={() => { if (post) setSlideSeek({ t: n.tc, n: Date.now() }); else if (video.current) video.current.currentTime = n.tc; }} className="shrink-0 font-mono text-xs font-semibold text-rework-fg">{revisionAt(n.tc, post)}</button>
                <span className="min-w-0 flex-1">
                  {n.text}
                  {hasDrawing(n.drawing) && <span className="mt-1 flex items-center gap-1 text-xs font-medium text-rework-fg"><Pencil className="size-3" />Пометка на кадре</span>}
                </span>
                <button type="button" onClick={() => setNotes((all) => all.filter((_, i) => i !== index))} aria-label="Удалить замечание"><X className="size-4" /></button>
              </div>)}
              {!post && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDrawingMode((value) => {
                        const next = !value;
                        if (next) video.current?.pause();
                        return next;
                      });
                    }}
                    aria-pressed={drawingMode}
                    className={drawingMode
                      ? "inline-flex min-h-10 items-center gap-2 rounded-full bg-foreground px-4 text-sm font-semibold text-background"
                      : "inline-flex min-h-10 items-center gap-2 rounded-full border border-border px-4 text-sm font-semibold"}
                  >
                    <Pencil className="size-4" />{drawingMode ? "Рисуйте на кадре" : "Нарисовать на кадре"}
                  </button>
                  {hasDrawing(draftDrawing) && <button type="button" onClick={() => setDraftDrawing(emptyDrawing())} className="min-h-10 rounded-full px-3 text-xs font-medium text-muted-foreground">Очистить</button>}
                </div>
              )}
              <form onSubmit={addNote} className="flex items-center gap-2 rounded-2xl border border-input bg-background p-1.5 pl-3 focus-within:ring-2 focus-within:ring-review">
                <span className="font-mono text-xs text-muted-foreground">{post ? `слайд ${slide + 1}` : formatDuration(time)}</span>
                <input ref={note} value={text} onChange={(e) => setText(e.target.value)} placeholder={post ? "Что исправить на этом слайде?" : "Что исправить в этом кадре?"} className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
                <button type="submit" disabled={!text.trim() && !hasDrawing(draftDrawing)} aria-label="Добавить замечание" className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground disabled:opacity-40"><Send className="size-4" /></button>
              </form>
              <p className="text-xs text-muted-foreground">{post ? "Откройте нужный слайд и напишите замечание." : "Остановите видео, при необходимости нарисуйте пальцем и добавьте замечание."}</p>
            </div>}
          </div>
        </div>
        {error && <p role="alert" className="shrink-0 bg-rework-soft px-4 py-2 text-sm text-rework-fg">{error}</p>}
        <footer className="flex shrink-0 gap-2 border-t border-border bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6">
          {!pending && <div className="flex flex-1 items-center justify-between gap-3 text-sm"><span className={item.mod?.verdict === "ok" ? "font-semibold text-done-fg" : "font-semibold text-rework-fg"}>{item.mod?.verdict === "ok" ? "Принято модератором" : "Возвращено на доработку"}</span><button type="button" onClick={onClose} className="rounded-full bg-muted px-4 py-2 font-semibold">Закрыть</button></div>}
          {pending && <>
          <button type="button" onClick={() => void verdict("rework")} disabled={busy || !videoReady || notes.length === 0}
            className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl border border-rework-bd bg-rework-soft px-3 text-sm font-semibold text-rework-fg disabled:opacity-45">
            <RotateCcw className="size-4" /><span>На доработку{notes.length ? ` · ${notes.length}` : ""}</span>
          </button>
          <button type="button" onClick={() => void verdict("ok")} disabled={busy || !videoReady}
            className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-45">
            <ShieldCheck className="size-4" /><span>{busy ? "Отправляем…" : videoReady ? "Принять" : "Загружаем видео…"}</span>
          </button>
          </>}
        </footer>
      </div>
    </div>
  );
}

/**
 * Чей ролик: «Алматы — @hapuov». Модератор смотрит команды двух офисов, и
 * отличать их по одним никам неудобно (просьба модератора 07.10.2026). Проект
 * дописываем, только если он не совпадает с командой — «Алматы — @tanys ·
 * Akorda News».
 */
function whoMade(task: Task, project: string | undefined): string {
  // С 08.10.2026 команд на экранах нет: «направление — монтажёр».
  const name = PEOPLE[task.editor].name;
  return `${project ?? teamOf(task.editor) ?? task.dept} — ${name}`;
}

/**
 * Можно ли ещё отменить решение (так же решает сервер): «норм» — пока ролик не
 * приняли и не выложили; замечания — пока монтажёр не сдал новую версию.
 * Публикацию в Instagram, которая уже пошла, не отменить.
 */
function undoable(item: SubTask): boolean {
  if (item.mod?.by !== ME_CLIENT) return false;
  if (item.autoPublishState === "publishing" || item.autoPublishState === "published") return false;
  if (item.mod.verdict === "ok") return item.status === "moderated" || (item.status === "approved" && item.approvedBy === ME_CLIENT && !item.link);
  const last = item.versions[item.versions.length - 1];
  return item.status === "rework" && !!last && item.round === last.n + 1;
}

async function undoVerdict(taskId: string, itemId: string, title: string) {
  try {
    const response = await montageApi.undoModeration(taskId, itemId);
    useStore.getState()._replace(taskId, toTask(response.task));
    notify(`«${title}» снова на проверке`, { icon: "check" });
  } catch (err) {
    notify((err as Error).message || "Не удалось отменить", { icon: "rework" });
  }
}
